import React from "react";
import { NO_VALUE, useKPITrendChart } from "../../hooks/useKPITrendChart";
import { KPIChartPoint } from "../../types";

interface KPITrendChartProps {
  series: KPIChartPoint[];
}

/**
 * Keeps the tooltip inside the card. Centred over its bar everywhere except
 * the two ends, where centring would hang half of it past the card's edge.
 */
const alignmentOf = (index: number, count: number): string => {
  if (index === 0) {
    return "translate-x-0";
  }

  if (index === count - 1) {
    return "-translate-x-full";
  }

  return "-translate-x-1/2";
};

const fillOf = (isCurrent: boolean, isActive: boolean): string => {
  if (isCurrent) {
    return isActive ? "fill-amber-500 dark:fill-amber-300" : "fill-amber-400";
  }

  return isActive
    ? "fill-secondary dark:fill-slate-400"
    : "fill-secondary/80 dark:fill-slate-500";
};

/**
 * One bar per month across the window, with the most recent month picked out.
 * A month with no figure keeps its slot and its label but draws nothing — the
 * gap is the statement, and its tooltip still answers. The scale and bar
 * geometry live in `useKPITrendChart`.
 *
 * Hovering is a mouse-only enhancement. The bars are deliberately not focus
 * targets — the whole card is already a button that flips to the formula, and
 * nesting thirteen more tab stops inside it would make that button unusable by
 * keyboard. What the chart says in words lives in the SVG's label instead.
 */
const KPITrendChart: React.FC<KPITrendChartProps> = ({ series }) => {
  const { geometry, activeIndex, setActiveIndex } = useKPITrendChart(series);

  if (geometry === null) {
    return null;
  }

  const {
    width,
    height,
    y,
    baseline,
    barWidth,
    centreOf,
    columnXOf,
    columnWidth,
    labelledIndexes,
    labelsCarryYear,
    summary,
  } = geometry;

  /**
   * Sits on top of the bar it describes rather than at a fixed height, so it
   * points at what is being hovered. A month with no bar anchors to the
   * baseline, where its bar would have started.
   */
  const tooltip = (index: number) => {
    const point = series[index];
    const top = point.value === null ? baseline : y(point.value);

    return (
      <div
        className={`pointer-events-none absolute z-20 mb-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 py-2 shadow-md dark:border-slate-600 dark:bg-slate-900 ${alignmentOf(index, series.length)}`}
        style={{
          left: `${(centreOf(index) / width) * 100}%`,
          bottom: `${((height - top) / height) * 100}%`,
        }}
      >
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {point.monthLabel}
        </div>
        <div className="text-sm font-semibold text-secondary dark:text-white">
          {point.valueLabel ?? NO_VALUE}
        </div>
        {point.workingLabel && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {point.workingLabel}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      <svg
        aria-label={summary}
        className="block h-auto w-full overflow-visible"
        role="img"
        viewBox={`0 0 ${width} ${height}`}
      >
        {series.map((point, index) => {
          if (point.value === null) {
            return null;
          }

          const top = y(point.value);

          return (
            <rect
              className={fillOf(
                index === series.length - 1,
                index === activeIndex,
              )}
              height={Math.max(2, baseline - top)}
              key={`${point.label}-${index}`}
              rx={2.5}
              width={barWidth}
              x={centreOf(index) - barWidth / 2}
              y={top}
            />
          );
        })}

        {labelledIndexes.map((index) => (
          <text
            className="fill-gray-500 text-[11px] dark:fill-gray-400"
            key={`${series[index].label}-${index}`}
            textAnchor="middle"
            x={centreOf(index)}
            y={height - 7}
          >
            {labelsCarryYear ? series[index].monthLabel : series[index].label}
          </text>
        ))}

        {/*
          Full-height catchers, drawn last so they sit above the bars. Hovering
          the column rather than the bar itself means a short month is no harder
          to read than a tall one, and a month with no bar at all still answers.
        */}
        {series.map((point, index) => (
          <rect
            className="fill-transparent"
            height={height}
            key={`catcher-${point.label}-${index}`}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            width={columnWidth}
            x={columnXOf(index)}
            y={0}
          />
        ))}
      </svg>

      {activeIndex !== null && tooltip(activeIndex)}
    </div>
  );
};

export default KPITrendChart;
